import React from "react";
import {Spinner, Toggle, useNotifications} from "peggirkit";
import {toggleLabel} from "peggirkit/dist/components/Input/Toggle/Single/Label/DefaultToggleLabel";
import {FormattedMessage, useIntl} from "react-intl";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import useProductInfo from "@widgets/pos/order/menu/product/context/ProductInfoProvider";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {AvailableMenu, ProductStock} from "@/data/entities/menu";
import {withProductStock} from "@/data/entities/menu/utils";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {api} from "@/data/api";

type MenuQueryData = {
    availableMenu: AvailableMenu,
};

/**
 * Marks the product as sold out or in stock. Takes effect on the ordering
 * site right away: sold-out products can't be added to the cart or ordered.
 * Only for products; options are not marked separately.
 */
const ProductStockToggle = () => {
    const intl = useIntl();
    const {notify} = useNotifications();
    const {dashboardLocation} = useRestaurant();
    const queryClient = useQueryClient();
    const {product, setProduct, loading} = useProductInfo();

    const {mutate: updateStock, isPending} = useMutation({
        mutationFn: (soldout: boolean) =>
            api.menu.updateProductStock(dashboardLocation.id, product.id, soldout),
        onSuccess: (stock: ProductStock) => {
            // The product in this modal
            setProduct({...product, soldout: stock.soldout});

            // The product buttons in the menu (delivery & takeaway). Keep the
            // update time, as the modal is remounted when it changes.
            queryClient
                .getQueryCache()
                .findAll({queryKey: ["menu", dashboardLocation.id]})
                .forEach(query => queryClient.setQueryData<MenuQueryData>(
                    query.queryKey,
                    data => data === undefined
                        ? data
                        : {...data, availableMenu: withProductStock(data.availableMenu, stock)},
                    {updatedAt: query.state.dataUpdatedAt},
                ));

            notifySuccess(
                intl.formatMessage({id: stock.soldout ? "productMarkedSoldOut" : "productMarkedInStock"}),
                notify,
            );
        },
        onError: () => {
            notifyDanger(intl.formatMessage({id: "systemError"}), notify);
        },
    });

    return (
        <div className={"flex flex-col items-start gap-1 border-b mb-1 pb-1"}>
            <div className={"flex flex-row items-center gap-2"}>
                <Toggle
                    type={"checkbox"}
                    checked={!product.soldout}
                    disabled={isPending || loading}
                    onChange={e => updateStock(!e.target.checked)}
                    label={toggleLabel(intl.formatMessage({id: "inStock"}))}
                />
                {isPending && <Spinner size={"sm"}/>}
            </div>
            <p className={"text-xs text-gray-500 dark:text-slate-400"}>
                <FormattedMessage id={product.soldout ? "productSoldOutExplanation" : "productInStockExplanation"}/>
            </p>
        </div>
    );
};

export default ProductStockToggle;